import { Button, DialogContent, DialogTitle, HelpText, Input, Label, Spinner } from '@atlas/ui-primitives'
import { type FormEvent, useEffect, useState } from 'react'
import type { MappingPreviewDTO } from '@/services/workfront-mappings.service'
import { PmAssignments, PreviewTotals } from './PreviewSummary'

/** The server refuses a shorter summary; refused here too, before the request. */
export const MIN_SUMMARY = 3

export interface PublishDialogProps {
	open: boolean
	/** The preview's numbers, shown once more; null while they are computed. */
	preview: MappingPreviewDTO | null
	/** The preview could not be computed: publishing is still allowed, without numbers. */
	previewFailed?: boolean
	submitting?: boolean
	onPublish: (summary: string) => void
	onCancel: () => void
	/** A user's display name, for the PMs the draft would assign. */
	nameOf?: (userId: string) => string
}

/** Publishing asks for a one-line summary, which History keeps, and shows what it will change. */
export function PublishDialog({
	open,
	preview,
	previewFailed,
	submitting,
	onPublish,
	onCancel,
	nameOf = (userId) => userId,
}: PublishDialogProps) {
	const [summary, setSummary] = useState('')
	useEffect(() => {
		if (open) setSummary('')
	}, [open])
	if (!open) return null

	const trimmed = summary.trim()
	const canSubmit = trimmed.length >= MIN_SUMMARY && !submitting
	const submit = (event: FormEvent) => {
		event.preventDefault()
		if (canSubmit) onPublish(trimmed)
	}

	return (
		<DialogContent size="lg" isOpen={open} onOpenChange={(next) => (next ? undefined : onCancel())}>
			<form onSubmit={submit}>
				<DialogTitle>Publish the draft</DialogTitle>
				<div className="mt-4">
					{preview ? (
						<div className="space-y-4">
							<PreviewTotals preview={preview} />
							{preview.pmAssignments.length ? <PmAssignments preview={preview} nameOf={nameOf} /> : null}
						</div>
					) : previewFailed ? (
						<p className="text-ds-sm text-ds-text-muted">The preview could not be computed.</p>
					) : (
						<Spinner aria-label="Computing the preview" />
					)}
				</div>
				<div className="mt-4">
					<Label htmlFor="publish-summary">Summary</Label>
					<Input
						id="publish-summary"
						value={summary}
						autoFocus
						maxLength={300}
						placeholder="Map new species labels"
						onChange={(event) => setSummary(event.target.value)}
					/>
					<HelpText>One line: History lists the version with it.</HelpText>
				</div>
				<div className="mt-6 flex justify-end gap-2">
					<Button variant="outline" onPress={onCancel}>
						Cancel
					</Button>
					<Button type="submit" isDisabled={!canSubmit}>
						{submitting ? 'Publishing…' : 'Publish'}
					</Button>
				</div>
			</form>
		</DialogContent>
	)
}
